import { cn } from "@/lib/utils";
import type { Player, SkillKey, LonerValue } from "../types";
import { SKILLS, MIN_PLAYER_NUMBER, MAX_PLAYER_NUMBER } from "../constants";
import { SkillCheckbox } from "./SkillCheckbox";
import { LonerSelect } from "./LonerSelect";
import { useLanguage } from "../i18n";

export interface PlayerCardProps {
  player: Player;
  onUpdateNumber: (number: number) => void;
  onToggleSkill: (skill: SkillKey) => void;
  onSetLoner: (enabled: boolean) => void;
  onSetLonerValue: (value: LonerValue) => void;
  onRemove: () => void;
  className?: string;
}

export function PlayerCard({
  player,
  onUpdateNumber,
  onToggleSkill,
  onSetLoner,
  onSetLonerValue,
  onRemove,
  className,
}: PlayerCardProps) {
  const { t } = useLanguage();
  const skillKeys = Object.keys(SKILLS) as SkillKey[];

  return (
    <div
      className={cn(
        "bg-bb-black border border-bb-gray-mid p-4 mb-3",
        className
      )}
    >
      <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
        <div className="flex items-center gap-2 font-display text-2xl text-bb-blood-red">
          <span>{t.player}</span>
          <input
            type="number"
            value={player.number}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val) && val >= MIN_PLAYER_NUMBER && val <= MAX_PLAYER_NUMBER) {
                onUpdateNumber(val);
              }
            }}
            min={MIN_PLAYER_NUMBER}
            max={MAX_PLAYER_NUMBER}
            className={cn(
              "w-12 font-display text-xl text-center p-1",
              "bg-bb-gray-mid border-2 border-bb-gray-mid rounded",
              "text-bb-gold",
              "focus:border-bb-gold focus:outline-none",
              "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            )}
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          className={cn(
            "font-condensed text-sm font-semibold uppercase tracking-wide",
            "px-3 py-1.5 transition-all",
            "bg-transparent text-bb-blood-bright border border-bb-blood-red",
            "hover:bg-bb-blood-red hover:text-bb-bone-white"
          )}
          aria-label={t.removePlayer}
        >
          ✕
        </button>
      </div>

      <div className="flex flex-wrap gap-3">
        {skillKeys.map((skill) => (
          <SkillCheckbox
            key={skill}
            skill={skill}
            checked={player.skills[skill] || false}
            onChange={() => onToggleSkill(skill)}
          />
        ))}
        <LonerSelect
          enabled={player.loner}
          value={player.lonerValue}
          onEnabledChange={onSetLoner}
          onValueChange={onSetLonerValue}
        />
      </div>
    </div>
  );
}
